import * as SRD from '@projectstorm/react-diagrams';
import { BaseEventProxy } from '@projectstorm/react-canvas-core';
import createEngine, { NodeModel } from '@projectstorm/react-diagrams';
import { Point } from '@projectstorm/geometry';
import NodeTypes from './model/workflowEditor/NodeTypes';
import {
  BotMessageFactory,
  UserIntentNodeFactory,
  RemoveLinkLabelFactory,
  RemoveLinkLabelModel,
  BotMessageNodeModel,
  UserIntentNodeModel,
  StartNodeFactory,
  LinkFactory,
  ConditionLabelFactory,
} from './model/workflowEditor';
import { StartNodeModel, ActionNodeModel } from './model/workflowEditor/nodes';
import { ActionNodeFactory } from './model/workflowEditor/nodes/NodeFactory';
import { PortFactory } from './model/workflowEditor/ports/PortFactory';

export interface Listener {
  id: string;
  callback: (event: BaseEventProxy) => void;
}

export class Application {
  protected activeModel: SRD.DiagramModel;

  protected diagramEngine: SRD.DiagramEngine;

  private listeners: Array<Listener> = [];

  constructor() {
    this.activeModel = new SRD.DiagramModel();
    this.diagramEngine = createEngine({
      registerDefaultDeleteItemsAction: false,
    });

    this.diagramEngine.setMaxNumberPointsPerLink(0);
    const state = this.diagramEngine.getStateMachine().getCurrentState();

    if (state) {
      // @ts-ignore
      state.dragNewLink.config.allowLooseLinks = false;
      // @ts-ignore
      state.dragNewLink.config.allowLinksFromLockedPorts = true;
    }

    this.diagramEngine
      .getNodeFactories()
      .registerFactory(new ActionNodeFactory());
    this.diagramEngine
      .getNodeFactories()
      .registerFactory(new UserIntentNodeFactory());
    this.diagramEngine
      .getNodeFactories()
      .registerFactory(new StartNodeFactory());
    this.diagramEngine
      .getNodeFactories()
      .registerFactory(new BotMessageFactory());
    this.diagramEngine
      .getLabelFactories()
      .registerFactory(new RemoveLinkLabelFactory());
    this.diagramEngine
      .getLabelFactories()
      .registerFactory(new ConditionLabelFactory());
    this.diagramEngine
      .getLinkFactories()
      .registerFactory(new LinkFactory());

      this.diagramEngine
      .getPortFactories()
      .registerFactory(new PortFactory());

    this.newModel();
  }

  public loadModel(flow: string) {
    this.activeModel = new SRD.DiagramModel();
    this.activeModel.deserializeModel(JSON.parse(flow), this.diagramEngine);
    var nodeList = this.activeModel.getNodes();
    nodeList.forEach(node => {
      this.addListenersToNode(node);
    });

    var linkList = this.activeModel.getLinks();
    linkList.forEach(link => {
      this.addListenersToLink(link);
    });
    this.addListenerToModel(this.activeModel);
    this.diagramEngine.setModel(this.activeModel);
  }

  public newModel() {
    this.activeModel = new SRD.DiagramModel();
    this.diagramEngine.setModel(this.activeModel);

    const startNode = new StartNodeModel({ color: 'rgb(0,192,255)' });
    startNode.setPosition(400, 100);

    // @ts-ignore
    this.activeModel.registerListener({
      eventDidFire: (event) => this.raiseListener(event),
    });

    this.diagramEngine.registerListener({
      repaintCanvas: () => { },
    });

    this.addListenerToModel(this.activeModel);
    this.addListenersToNode(startNode);
    this.activeModel.addAll(startNode);
  }

  public addListenersToNode(node: SRD.NodeModel<SRD.NodeModelGenerics>) {
    // @ts-ignore
    node.registerListener({
      eventDidFire: (event) => this.raiseListener(event),
    })
  }

  public addListenersToLink(link: any) {
    // @ts-ignore
    link.registerListener({
      eventDidFire: (event: any) => this.raiseListener(event),
    });
    // @ts-ignore
    link.registerListener({
      // @ts-ignore
      selectionChanged: (e) => {
        if (e.isSelected) {
          const label = new RemoveLinkLabelModel({ label: 'id' });
          label.registerListener({
            linkDeleted: (event) => {
              this.repaint();
            },
          });
          // @ts-ignore
          link.addLabel(label);
        } else {
          // @ts-ignore
          link.labels = link.labels.filter((x: any) => x.options.type !== 'customLabel');
        }
      },
      // @ts-ignore
      entityRemoved: (e) => {
        //
      },
    });
  }

  public registerListener(listener: Listener) {
    this.listeners.push(listener);
  }

  public raiseListener(event: BaseEventProxy) {
    const listenerToRaise = this.listeners.filter(
      (l) => l.id === event.function,
    );
    listenerToRaise.forEach((listener) => {
      listener.callback(event);
    });
  }

  public serializeDiagramToJson(): string {
    return JSON.stringify(this.activeModel.serialize());
  }

  public createNode(type: NodeTypes, position: Point) {
    let node: NodeModel | undefined;
    switch (type) {
      case NodeTypes.botMessageNode:
        node = new BotMessageNodeModel();
        break;
      case NodeTypes.userIntentNode:
        node = new UserIntentNodeModel();
        break;
      case NodeTypes.actionNode:
        node = new ActionNodeModel();
        break;
      default:
        break;
    }

    if (node !== undefined) {
      // @ts-ignore
      this.addListenersToNode(node);
      node.setPosition(position);
      this.activeModel.addNode(node);
    }
  }

  public getActiveDiagram(): SRD.DiagramModel {
    return this.activeModel;
  }

  public getDiagramEngine(): SRD.DiagramEngine {
    return this.diagramEngine;
  }

  public repaint() {
    this.diagramEngine.repaintCanvas();
  }

  public setZoom(percent: number) {
    const currentZoomLevel = this.activeModel.getZoomLevel();
    this.activeModel.setZoomLevel(currentZoomLevel + percent);
    this.repaint();
  }

  addListenerToModel(diagramModel: SRD.DiagramModel) {
    // @ts-ignore
    diagramModel.registerListener({
      linksUpdated: (event) => {
        this.linksUpdated(event);
      },
    });
  }

  linksUpdated(event: any) {
    event.link.registerListener({
      eventDidFire: (event: any) => this.raiseListener(event),
    });
    // @ts-ignore
    event.link.registerListener({
      // @ts-ignore
      selectionChanged: (e) => {
        if (e.isSelected) {
          const label = new RemoveLinkLabelModel({ label: 'id' });
          label.registerListener({
            linkDeleted: (event) => {
              this.repaint();
            },
          });
          // @ts-ignore
          event.link.addLabel(label);
        } else {
          // @ts-ignore
          event.link.labels = event.link.labels.filter((x: any) => x.options.type !== 'customLabel');
        }
      },
      // @ts-ignore
      entityRemoved: (e) => {
        //
      },
    });
  }
}

export default new Application();
